import React from 'react';
import { SessionListResponse } from 'interfaces/sessions';
import { SessionV2Columns, MessagingDataColumns } from 'interfaces/table';
import { MessageBubble } from './MessageBubble';

interface TextPreviewProps {
  session: SessionListResponse;
  isSelected: boolean;
  onClick?: () => void;
}

interface Message {
  body: string;
  time: number;
  isAgent: boolean;
}

const styles = {
  messageContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  message: {
    maxWidth: '85%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    alignSelf: (isAgent: boolean) => isAgent ? 'flex-end' : 'flex-start',
  },
  timestamp: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
    textAlign: (isAgent: boolean) => isAgent ? 'right' : 'left',
  }
};

export const TextPreview: React.FC<TextPreviewProps> = ({ session, isSelected }) => {
  const parseJsonSafely = (str: string | undefined): any[] => {
    if (!str) return [];
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return [];
    }
  };

  const parseTimesSafely = (str: string | undefined): number[] => {
    if (!str) return [];
    return str.split(',').map(t => parseInt(t)).filter(t => !isNaN(t));
  };

  const agentBodies = parseJsonSafely(session.result.get(MessagingDataColumns.AGENT_BODIES_STR) as string);
  const agentTimes = parseTimesSafely(session.result.get(MessagingDataColumns.AGENT_TIMES_STR) as string);
  const customerBodies = parseJsonSafely(session.result.get(MessagingDataColumns.CUSTOMER_BODIES_STR) as string);
  const customerTimes = parseTimesSafely(session.result.get(MessagingDataColumns.CUSTOMER_TIMES_STR) as string);

  const allMessages: Message[] = [
    ...agentBodies.map((body: string, i: number) => ({
      body,
      time: agentTimes[i],
      isAgent: true
    })),
    ...customerBodies.map((body: string, i: number) => ({
      body,
      time: customerTimes[i],
      isAgent: false
    }))
  ].sort((a, b) => a.time - b.time);

  const formatTimestamp = (seconds: number) => {
    if (!session.start) return '';
    const messageTime = new Date(session.start.getTime() + (seconds * 1000));
    return messageTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={styles.messageContainer}>
      {allMessages.map((msg, i) => (
        <div
          key={i}
          style={{
            ...styles.message,
            alignSelf: styles.message.alignSelf(msg.isAgent)
          }}
        >
          <MessageBubble isAgent={msg.isAgent}>
            {msg.body}
          </MessageBubble>
          <div 
            style={{
              ...styles.timestamp,
              textAlign: styles.timestamp.textAlign(msg.isAgent)
            }}
          >
            {formatTimestamp(msg.time)}
          </div>
        </div>
      ))}
    </div>
  );
};
